import { FC, createContext, useContext, useState } from 'react';
import { Outlet } from 'react-router-dom';
import PersistentCanvas from './PersistentCanvas';
import styles from '../../assets/css/preview.module.scss';

export type StudioSlotName =
  | 'canvasOverlay'
  | 'belowCanvas'
  | 'leftColExtras'
  | 'rightCol'
  | 'footer';

export type StudioLayoutContextValue = Record<StudioSlotName, HTMLDivElement | null> & {
  setCanvasContainerClass: (cls: string) => void;
};

const StudioLayoutContext = createContext<StudioLayoutContextValue | null>(null);

export const useStudioSlots = () => {
  const ctx = useContext(StudioLayoutContext);
  if (!ctx) {
    throw new Error('useStudioSlots must be used inside <StudioLayout />');
  }
  return ctx;
};

// Shared layout for the Preview Insole + Personalization routes. Owns the
// single PersistentCanvas (one WebGL context for the whole app) and exposes
// DOM slot refs so the active route can portal its overlay/below-canvas/
// left-col-extras/right-col/footer content into the shared layout without
// re-mounting the Canvas. This is the key to avoiding iOS Safari WebGL
// context exhaustion when toggling between routes.
const StudioLayout: FC = () => {
  const [canvasContainerClass, setCanvasContainerClass] = useState('');
  const [canvasOverlay, setCanvasOverlay] = useState<HTMLDivElement | null>(null);
  const [belowCanvas, setBelowCanvas] = useState<HTMLDivElement | null>(null);
  const [leftColExtras, setLeftColExtras] = useState<HTMLDivElement | null>(null);
  const [rightCol, setRightCol] = useState<HTMLDivElement | null>(null);
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);

  return (
    <StudioLayoutContext.Provider
      value={{ canvasOverlay, belowCanvas, leftColExtras, rightCol, footer, setCanvasContainerClass }}
    >
      <div className={styles.previewContainer}>
        <div className={styles.previewLeftCol}>
            <div className={`${styles.canvasContainer} ${canvasContainerClass}`}>
              <div className={styles.canvasContainerWrapper}>
                <PersistentCanvas />
              </div>
              <div ref={setCanvasOverlay} />
            </div>
            <div ref={setBelowCanvas} />
          <div ref={setLeftColExtras} />
        </div>
        <div className={styles.previewRightCol}>
          <div className={styles.previewFormContainer} ref={setRightCol}>
          </div>
        </div>
      </div>
      <div className={styles.previewFormFooter} ref={setFooter}>
      </div>
      <Outlet />
    </StudioLayoutContext.Provider>
  );
};

export default StudioLayout;
